import React, {useEffect, useRef} from "react";
import {Hit as AlgoliaHit} from "instantsearch.js/es/types/results";
import {Highlight} from "react-instantsearch-hooks-web";
import Util from '../../../../../../../src/js/Util';

type HitProps = {
    hit: AlgoliaHit<{
        post_title: string;
        price: number;
        max_qty?: number;
    }>;
    index: number;
};

function ProductCard( { hit, index }: HitProps) {
    const badge = hit.badge[SUMA.language] || [];
    const details_button = 'details_button' in hit ? hit.details_button : true;
    const images = hit.images || [];
    const permalink = hit.permalink[SUMA.language] || '#';
    const post_id = hit.post_id || null;
    const price = hit.price || 0;
    const sale_price = hit.sale_price || 0;
    const regular_price = hit.regular_price || 0;
    const on_sale = hit.on_sale || 0;
    const has_sale_price = on_sale && sale_price > 0 && sale_price < regular_price;
    let price_html_override = hit.price_override ?? '';

    if ( '--' === price_html_override )
        price_html_override = '';

    const price_html = (price).toLocaleString( 'en-US', {
        style: 'currency',
        currency: 'USD',
    } );
    const sale_price_html = (sale_price).toLocaleString( 'en-US', {
        style: 'currency',
        currency: 'USD',
    } );
    const regular_price_html = (regular_price).toLocaleString( 'en-US', {
        style: 'currency',
        currency: 'USD',
    } );

    const sku = hit.sku || null;
    const stock = hit.stock || null;
    const max_qty = hit.max_qty || 99;
    const subtitle = hit.subtitle[SUMA.language] || null;
    const title = hit.post_title[SUMA.language] || null;
    const metal = hit.metal[SUMA.language] || null;
    const price_class = metal ? 'product-price--metal' : '';
    const is_coming_soon = 'key' in badge && badge.key === 'coming-soon';
    const show_primary_button = stock === 'instock' && ! is_coming_soon;

    const swiperRef = useRef(null);
    const swiperInstanceRef = useRef(null);
    useEffect( () => {

        if ( swiperRef.current && !swiperInstanceRef.current ) {

            swiperInstanceRef.current = new Swiper( swiperRef.current, {
                allowTouchMove: true,
                lazy: true,
                slidesPerView: 1,
                spaceBetween: 0,
                pagination: {
                    clickable: true,
                    el: swiperRef.current.querySelector('.swiper-pagination')
                },
            } );
        }

        return () => {

            if ( swiperInstanceRef.current ) {
                swiperInstanceRef.current.destroy();
                swiperInstanceRef.current = null;
            }
        };
    }, [hit] );

    return (
        <div className="product-card" data-refresh-id={post_id} data-version={index} key={post_id}>
            { badge && badge.key &&
                <div className="product-badge-container">
                    <div className={`product-badge product-badge--${ badge.key }`}>
                        { badge.label }
                    </div>
                </div>
            }

            { images.length > 0 &&
                <a href={permalink} className="product-images" title="">
                    <div ref={swiperRef} className="swiper product-images">
                        <div className="swiper-wrapper">
                            { images.map( (img, i) => {
                                return (
                                    <div className={`swiper-slide slide-${i}`} key={i}>
                                        <div className="product-image-container" data-src={img}>
                                            { i === 0 && img &&
                                                <img src={img} loading={index < 4 ? 'eager' : 'lazy'} fetchpriority={index < 4 ? 'high' : 'low'} />
                                            }
                                        </div>
                                    </div>
                                );
                            } ) }
                        </div>
                        <div className="swiper-pagination"></div>
                    </div>
                </a>
            }

            <div className="product-details">

                <div className="product-name">

                    { title &&
                        <a className="product-title" href={`${ permalink }`} target="_top" title="View Product" dangerouslySetInnerHTML={{ __html: title }}></a>
                    }

                    { subtitle &&
                        <div className="product-subtitle">
                            { subtitle }
                        </div>
                    }

                </div>

                { price && stock === 'instock' &&
                    <div className={`product-price ${price_class}`} data-refresh-strategy="event">
                        {
                            (price_html_override && price_html_override !== '' &&
                                <div dangerouslySetInnerHTML={{ __html: price_html_override}}></div>)
                            ||
                            (has_sale_price ?
                                <div>
                                    <del aria-hidden="true">
                                    <span className="woocommerce-Price-amount amount">
                                        <bdi>
                                            { regular_price_html }
                                        </bdi>
                                    </span>
                                    </del>
                                    <ins>
                                    <span className="woocommerce-Price-amount amount">
                                        <bdi>
                                            { sale_price_html }
                                        </bdi>
                                    </span>
                                    </ins>
                                </div> : <div> { price_html } </div>)
                        }
                        <div className="product-best-price">Best Price</div>
                    </div>
                }

            </div>

            <div className="product-actions">

                <div className="product-actions-button-set">

                    { show_primary_button &&
                        <a className="button product_type_simple add_to_cart_button ajax_add_to_cart product-actions-button product-actions-button--primary product-actions-button--add-to-cart"
                           href={`?add-to-cart=${ post_id }`}
                           data-quantity="1"
                           data-product_id={`${ post_id }`}
                           data-product_sku={`${ sku }`}
                           aria-label={`${Util.translate('Add')} ${ title } ${Util.translate('to your cart')}`}
                           rel="nofollow"
                        >
                            {Util.translate('Buy Now')}
                        </a>
                    }

                    { details_button &&
                        <a className="product-actions-button product-actions-button--secondary" href={`${ permalink }`}>
                            {Util.translate('Details')}
                        </a>
                    }

                </div>

                <div className="product-actions-options">
                    <div className="option-action option-action-info">
                        <a href={`${ permalink }`} target="_top" title="{algolia_catalog_translations['Details']}">
                            <i className="fa-regular fa-circle-info"></i>
                        </a>
                    </div>
                    <div className="quantity">
                        <button className="qty-btn qty-btn-dec" type="button"><i className="fa-regular fa-minus"></i></button>
                        <input className="qty" data-product-id={`${ post_id }`} max={`${max_qty}`} min="1" name="qty" type="number" defaultValue="1"/>
                        <button className="qty-btn qty-btn-inc" type="button"><i className="fa-regular fa-plus"></i></button>
                    </div>
                    <div className="option-action option-action-cart">
                        <a href="/cart/" target="_top" title="My Cart">
                            <i className="fa-regular fa-cart-shopping"></i>
                        </a>
                    </div>
                </div>

            </div>

        </div>
    );
}

export { HitProps, ProductCard };
